import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import moment from "moment";
import {
  formatNumber,
  truncateToTwoDecimals,
} from "../../../../../../utility/helper";
import { useSelector } from "react-redux";

const CustomTooltip = ({ active, payload }) => {
  const { currency } = useSelector((state) => state.user);
  if (active && payload && payload.length) {
    const { price, originalDate } = payload[0]?.payload;
    return (
      <div
        style={{
          padding: "10px",
          color: "#333",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
        }}
      >
        <div className="flex gap-2">
          <strong style={{ margin: 0, color: "#333" }}>Price:</strong>
          <p style={{ color: "#333" }}>
            {formatNumber(truncateToTwoDecimals(Number(price)))}{" "}
            {currency || "CHF"}
          </p>
        </div>
        <div className="flex gap-2">
          <strong style={{ margin: 0, color: "#333" }}>Date:</strong>
          <p style={{ color: "#333" }}>
            {moment(originalDate).format("DD/MM/YY")}
          </p>
        </div>
      </div>
    );
  }
  return null;
};

const ReturnComponent = ({
  graphROI,
  companyDetails,
  selectedAsset,
  isNegativeROI,
  userAssetLoading,
  companyLoading,
  graphLoading,
}) => {
  const transformedData = graphROI?.chart_data
    ?.map((item) => ({
      date: new Date(item.date).getTime(),
      value: item.value,
      price: item.closing_price,
      originalDate: item.date,
    }))
    ?.sort((a, b) => a?.date - b?.date);

  const theme = useSelector((state) => state.theme.mode);
  const { currency } = useSelector((state) => state.user);

const generateTicks = () => {
  if (!transformedData || transformedData.length === 0) return [];

  const maxTicks = 10;
  const n = transformedData.length;

  if (n <= maxTicks) return transformedData.map((d) => d.date);

  const ticks = [];
  const step = (n - 1) / (maxTicks - 1);

  for (let i = 0; i < maxTicks; i++) {
    const index = Math.round(i * step);
    ticks.push(transformedData[index].date);
  }

  return ticks;
};


  const generateNiceTicks = (data, maxTicks = 5) => {
    if (!data || data.length === 0) return [];

    const values = data.map((d) => d.price);
    let min = Math.min(...values);
    let max = Math.max(...values);

    // For 2 points, add small padding
    if (min === max) {
      min = min * 0.9;
      max = max * 1.1;
    }

    const range = max - min;
    const roughStep = range / (maxTicks - 1) || 1;
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    let step = magnitude;
    if (roughStep / magnitude >= 5) step = 5 * magnitude;
    else if (roughStep / magnitude >= 2) step = 2 * magnitude;

    const start = Math.floor(min / step) * step;
    const end = Math.ceil(max / step) * step;

    const ticks = [];
    for (let i = start; i <= end; i += step) {
      ticks.push(i);
    }
    return ticks;
  };

  const formatTick = (value) => {
    const format = (num, suffix) => {
      let str = num.toFixed(1);
      if (str.endsWith(".0")) str = str.slice(0, -2);
      return str + suffix;
    };

    if (value >= 1e12 || value <= -1e12) {
      return format(value / 1e12, "T");
    } else if (value >= 1e9 || value <= -1e9) {
      return format(value / 1e9, "B");
    } else if (value >= 1e6 || value <= -1e6) {
      return format(value / 1e6, "M");
    }
    return formatNumber(truncateToTwoDecimals(Number(value)));
  };

  const quote = companyDetails?.quote?.[0] || {};

  return (
    <div className="md:flex gap-4 lg:gap-16 md:space-y-0 space-y-5 mt-8">
      {/* === Left Section (Chart) === */}
      <div className="border border-lightPrimary/40 dark:border-info/40 p-5 lg:w-2/3 w-full dark:bg-custom-radial bg-white">
        {userAssetLoading || graphLoading ? (
          <div className="animate-pulse space-y-4">
            <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 rounded mx-auto"></div>
            <div className="h-[400px] bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        ) : (
          <>
            <h2
              className={`${
                isNegativeROI
                  ? "text-warning"
                  : "text-lightPrimary dark:text-primary-gradient"
              } text-2xl font-semibold text-center`}
            >
              {userAssetLoading ? (
                <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              ) : selectedAsset?.name ? (
                `Stock - ${selectedAsset?.name}`
              ) : (
                "No Stock found"
              )}
            </h2>
            <ResponsiveContainer width="100%" height={500}>
              <LineChart
                data={transformedData}
                margin={{ top: 60, right: 20, left: 0, bottom: 5 }}
              >
                <defs>
                  {isNegativeROI ? (
                    <linearGradient
                      id="colorGradient"
                      x1="0"
                      y1="0"
                      x2="1"
                      y2="0"
                    >
                      <stop offset="0%" stopColor="#f0bf77ff" />
                      <stop offset="50%" stopColor="#f19019" />
                      <stop offset="100%" stopColor="#EA8100" />
                    </linearGradient>
                  ) : theme === "light" ? (
                    <linearGradient
                      id="colorGradient"
                      x1="0"
                      y1="0"
                      x2="1"
                      y2="0"
                    >
                      <stop offset="0%" stopColor="#72b4eb" />
                      <stop offset="40%" stopColor="rgb(87, 136, 241)" />
                      <stop offset="100%" stopColor="rgb(71, 130, 240)" />
                    </linearGradient>
                  ) : (
                    <linearGradient
                      id="colorGradient"
                      x1="0"
                      y1="0"
                      x2="1"
                      y2="0"
                    >
                      <stop offset="0%" stopColor="#cce9ff" />
                      <stop offset="40%" stopColor="rgb(130, 188, 235)" />
                      <stop offset="60%" stopColor="#6eb6f1" />
                      <stop offset="80%" stopColor="#3a91d9" />
                      <stop offset="100%" stopColor="#47C1F0" />
                    </linearGradient>
                  )}
                </defs>

                <CartesianGrid strokeDasharray="0 1" stroke="#ccc" />

                <XAxis
                  dataKey="date"
                  // domain={["dataMin", "dataMax"]}
                  axisLine={false}
                  tickLine={false}
                  ticks={generateTicks()}
                  tickFormatter={(value) => moment(value).format("DD/MM/YY")}
                  tick={{
                    fill:
                      theme === "dark"
                        ? "rgb(206, 206, 206)"
                        : "rgb(77, 76, 83)",
                    fontSize: 12,
                  }}
                  padding={{ left: 20, right: 20 }}
                />

                <YAxis
                  axisLine={false}
                  tickLine={false}
                  ticks={generateNiceTicks(transformedData, 5)}
                  domain={["dataMin", "dataMax"]}
                  allowDecimals={false}
                  tick={{
                    fill:
                      theme === "dark"
                        ? "rgb(206, 206, 206)"
                        : "rgb(77, 76, 83)",
                    fontSize: 12,
                  }}
                  tickFormatter={formatTick}
                />

                <Tooltip content={<CustomTooltip />} cursor={false} />
                <Line
                  type="linear"
                  dataKey="price"
                  stroke="url(#colorGradient)"
                  dot={false}
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </>
        )}
      </div>

      <div className="relative dark:bg-custom-radial overflow-x-auto lg:w-1/3 w-full border border-lightPrimary/40 dark:border-info/40 bg-white">
        {companyLoading || userAssetLoading ? (
          <div className="animate-pulse p-5 space-y-4">
            <div className="h-7 w-40 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-4 w-56 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-4 w-48 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-4 w-52 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-20 w-full bg-gray-200 dark:bg-gray-700 rounded mb-7"></div>

            <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-4 w-48 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-4 w-56 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-4 w-48 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-4 w-52 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        ) : (
          <>
            <h2
              className={`${
                isNegativeROI
                  ? "text-warning"
                  : "text-lightPrimary dark:text-primary-gradient"
              } text-2xl font-semibold py-4 pl-4`}
            >
              Company Overview
            </h2>
            <div
              className={`${
                isNegativeROI
                  ? "text-warning"
                  : "text-lightPrimary dark:text-primary-gradient"
              } p-5`}
            >
              <div className="mb-5 flex items-center gap-2">
                <span className="text-info dark:text-grey">📌</span>
                <span>{companyDetails?.profile?.company_name || "N/A"}</span>
              </div>
              <div className="mb-5 flex items-center gap-2">
                <span className="text-info dark:text-grey">👑</span>
                <span>{companyDetails?.profile?.ceo || "N/A"}</span>
              </div>
              <div className="mb-5 flex items-center gap-2">
                <span className="text-info dark:text-grey">🏦</span>
                <span>{companyDetails?.profile?.sector || "N/A"}</span>
              </div>
              <div className="mb-5">
                {companyDetails?.profile?.description
                  ? companyDetails?.profile?.description
                      .split(" ")
                      .slice(0, 40)
                      .join(" ") + "..."
                  : "N/A"}
              </div>
              <div className="">
                <span className="text-2xl">Key Indicators</span>
                <div className="mt-6 flex items-center gap-2">
                  <span className="text-info dark:text-grey">📊</span>
                  <span>
                    P/E Ratio: {truncateToTwoDecimals(quote?.pe) || "21.45"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-info dark:text-grey">📈</span>
                  <span>
                    Earnings Per Share (EPS):{" "}
                    {truncateToTwoDecimals(quote?.eps) || "17.06"}{" "}
                    {currency ? currency : ""}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-info dark:text-grey">📈</span>
                  <span>
                    52-Week High:{" "}
                    {truncateToTwoDecimals(quote?.yearHigh) || "436.00"}{" "}
                    {currency ? currency : ""}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-info dark:text-grey">📉</span>
                  <span>
                    52-Week Low:{" "}
                    {truncateToTwoDecimals(quote?.yearLow) || "231.80"}{" "}
                    {currency ? currency : ""}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReturnComponent;
